import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { couponsVisible } from '../../lib/features'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { User, LogOut, Ticket, ChevronDown, UserCog, ExternalLink, Tag } from 'lucide-react'
import { useAuth } from '../../store/authStore'
import { LanguageSwitcher } from './LanguageSwitcher'
import { cn } from '../../lib/cn'

function initials(name?: string, lastname?: string, email?: string): string {
  const a = (name || '').trim()
  const b = (lastname || '').trim()
  if (a || b) return ((a[0] ?? '') + (b[0] ?? '')).toUpperCase() || 'U'
  return (email || 'U').slice(0, 1).toUpperCase()
}

function AccountMenu() {
  const { t } = useTranslation()
  const { locale = 'it' } = useParams()
  const navigate = useNavigate()
  const status = useAuth((s) => s.status)
  const user = useAuth((s) => s.user)
  const signOut = useAuth((s) => s.signOut)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  if (status === 'unknown') return <span className="h-9 w-9" />

  if (status !== 'authenticated' || !user) {
    return (
      <Link
        to={`/${locale}/login`}
        className="inline-flex h-9 items-center gap-1.5 rounded-full bg-ink-900 px-4 text-sm font-semibold text-white transition hover:bg-ink-800"
      >
        <User className="h-4 w-4" /> {t('nav.login')}
      </Link>
    )
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-9 items-center gap-2 rounded-full bg-ink-100/80 py-1 pl-1 pr-2.5 text-sm font-semibold text-ink-800 transition hover:bg-ink-100"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-600 text-[11px] font-bold text-white">
          {initials(user.name, user.lastname, user.email)}
        </span>
        <span className="hidden max-w-[120px] truncate sm:block">{user.name || user.email}</span>
        <ChevronDown className={cn('h-4 w-4 text-ink-400 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-40 mt-2 w-56 overflow-hidden rounded-xl border border-ink-100 bg-white shadow-pop">
          <div className="border-b border-ink-100 px-4 py-3">
            <p className="truncate text-sm font-semibold text-ink-900">
              {[user.name, user.lastname].filter(Boolean).join(' ') || user.email}
            </p>
            <p className="truncate text-xs text-ink-400">{user.email}</p>
          </div>
          <Link
            to={`/${locale}/bookings`}
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
          >
            <Ticket className="h-4 w-4 text-brand-600" /> {t('nav.myBookings')}
          </Link>
          {couponsVisible() && (
            <Link
              to={`/${locale}/coupons`}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              <Tag className="h-4 w-4 text-brand-600" /> {t('nav.coupons')}
            </Link>
          )}
          <Link
            to={`/${locale}/profile`}
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
          >
            <UserCog className="h-4 w-4 text-brand-600" /> {t('nav.profile')}
          </Link>
          <button
            onClick={async () => {
              setOpen(false)
              await signOut()
              navigate(`/${locale}`)
            }}
            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-ink-700 hover:bg-ink-50"
          >
            <LogOut className="h-4 w-4 text-ink-400" /> {t('nav.logout')}
          </button>
        </div>
      )}
    </div>
  )
}

export function Header() {
  const { t } = useTranslation()
  const { locale = 'it' } = useParams()
  const { pathname } = useLocation()
  const onPayment = pathname.endsWith('/payment') // hide the legacy fallback during payment
  return (
    <header className="sticky top-0 z-30 border-b border-ink-100 bg-white/85 pt-[env(safe-area-inset-top)] backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6 xl:max-w-6xl">
        <Link to={`/${locale}`} className="flex items-center" aria-label="Garage San Marco">
          <img src="/brand/logo.svg" alt="Garage San Marco" className="h-9 w-auto sm:h-10" />
        </Link>
        <div className="flex items-center gap-2">
          {!onPayment && (
            <a
              href={`https://booking.garagesanmarco.it/${locale}`}
              target="_blank"
              rel="noreferrer"
              className="hidden items-center gap-1.5 rounded-full bg-brand-50 px-3.5 py-1.5 text-xs font-semibold text-brand-700 ring-1 ring-inset ring-brand-200 transition-colors hover:bg-brand-100 hover:ring-brand-300 sm:inline-flex"
            >
              {t('common.classicSite')} <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
          <LanguageSwitcher />
          <AccountMenu />
        </div>
      </div>
    </header>
  )
}
